import { useCallback, useEffect, useState } from 'react'
import { useTranslate } from 'react-polyglot'

import { useAccountContext } from 'components/contexts/AccountContext'
import config from 'config'
import { useLocale, usePartnerFeatures } from 'hooks'
import { PartnerMenuItem } from 'types/PartnerMenuItem'

const partnerExchangeLocaleMap = new Map<string, string>([
  ['en_US', 'English'],
  ['en_GB', 'English'],
  ['de_DE', 'German'],
  ['it_IT', 'Italian'],
  ['fr_FR', 'French'],
  ['es_ES', 'Spanish'],
])

const usePartnerLinks = () => {
  const t = useTranslate()
  const locale = useLocale()
  const { accountKey: selectedAccountKey } = useAccountContext()
  const { loading, hasGoToResolve, isIngramAccount } = usePartnerFeatures()
  const exchangeLocale = partnerExchangeLocaleMap.get(locale) || 'English'

  const [gtaPartnerEditionLinks, setGtaPartnerEditionLinks] = useState<PartnerMenuItem[]>([])
  const [partnerExchangeLinks, setPartnerExchangeLinks] = useState<PartnerMenuItem[]>([])
  const [learnMoreLinks, setLearnMoreLinks] = useState<PartnerMenuItem[]>([])

  const getSalesLink = useCallback(
    () =>
      !isIngramAccount
        ? [
            {
              key: 'sales',
              title: t('Sales'),
              href: `${config.PARTNER_EXCHANGE_URL}/${exchangeLocale}/AuthorizedAgent/sfdc_deal_registration/overview.aspx`,
            },
          ]
        : [],
    [isIngramAccount, exchangeLocale, t],
  )

  useEffect(() => {
    setGtaPartnerEditionLinks([
      {
        key: 'managed-accounts',
        title: t('Managed accounts'),
        href: `${config.PARTNER_ADMIN_URL}/${selectedAccountKey}/accounts`,
      },
      {
        key: 'partner-users',
        title: t('Partner users'),
        href: `${config.PARTNER_ADMIN_URL}/${selectedAccountKey}/people/users`,
      },
      {
        key: 'partner-account-settings',
        title: t('Partner account settings'),
        href: `${config.PARTNER_ADMIN_URL}/${selectedAccountKey}/settings/support-contact`,
      },
    ])
    setPartnerExchangeLinks([
      {
        key: 'marketing',
        title: t('Marketing'),
        href: hasGoToResolve
          ? `${config.PARTNER_EXCHANGE_URL}/prm/${exchangeLocale}/s/assets?collectionId=43032`
          : `${config.PARTNER_EXCHANGE_URL}/${exchangeLocale}/AuthorizedAgent/marketing/overview.aspx`,
      },
      ...getSalesLink(),
    ])
    setLearnMoreLinks([
      {
        key: 'partner-training',
        title: t('Partner training'),
        href: hasGoToResolve
          ? 'https://lmi.docebosaas.com/pages/225/goto-resolve-product-training-resource'
          : `${config.PARTNER_EXCHANGE_URL}/prm/${exchangeLocale}/c/trainingoverview`,
      },
      {
        key: 'get-support',
        title: t('Get support'),
        href: `${config.PARTNER_EXCHANGE_URL}/${exchangeLocale}/AuthorizedAgent/sfdc_support_cases/support_cases.aspx`,
      },
    ])
  }, [exchangeLocale, hasGoToResolve, selectedAccountKey, getSalesLink, t])

  return { gtaPartnerEditionLinks, partnerExchangeLinks, learnMoreLinks, loading }
}

export default usePartnerLinks
